import { isPreOrderActive } from '@cart-services/utils/findPreOrderProduct'
import { Amount } from '@cart-ui/Amount/Amount'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { Box } from '@pets-ui/Box/Box'
import { Chip } from '@pets-ui/Chip/Chip'
import { HStack } from '@pets-ui/HStack/HStack'
import { NumberInput } from '@pets-ui/NumberInput/NumberInput'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { isNewProduct } from '../utils/new-product-filter'

const LOW_STOCK_THRESHOLD = 25

const styles = stylex.create({
  inputWrapper: {
    boxSizing: 'border-box',
    borderLeftColor: colors.grey300,
    borderLeftStyle: 'solid',
    borderLeftWidth: 1,
    borderRightColor: colors.grey300,
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    height: '100%',
  },
  input: {
    'backgroundColor': {
      'default': 'transparent',
      ':disabled': colors.grey100,
    },
    'color': {
      'default': colors.grey800,
      ':disabled': colors.grey500,
    },
    'textAlign': 'center',
    'width': 26,
    '::-webkit-inner-spin-button': {
      margin: 0,
      appearance: 'none',
    },
    '::-webkit-outer-spin-button': {
      margin: 0,
      appearance: 'none',
    },
  },
})

export interface ProductVariationRowProps {
  variation: Variation
  currentQuantity: number
  onQuantityChange: (quantity?: number) => void
  id?: string
}

export const ProductVariationRow = (props: ProductVariationRowProps) => {
  const { variation, currentQuantity, onQuantityChange, id } = props
  const remainingQuantity = variation.stock - currentQuantity
  const shouldShowOriginalPrice = variation.original_price !== 0 && variation.original_price > variation.sale_price
  const isNew = isNewProduct(variation.product_create_time)
  const isPreOrder = isPreOrderActive(variation.pre_order_info)

  return (
    <HStack id={id} gap={2} alignItems="center" justifyContent="space-between" p={2} minHeight="52px">
      <Box flex={1} minWidth={0}>
        <HStack gap={1} alignItems="center">
          <Text color={colors.grey800} bold lineClamp={2}>
            {variation.name}
          </Text>
          {isNew && (
            <Chip variant="square" size="small" color="violet" isOutlined isSoft flexShrink={0}>
              new
            </Chip>
          )}
          {isPreOrder && (
            <Chip variant="square" size="small" color="red" isOutlined isSoft>
              預購
            </Chip>
          )}
        </HStack>
      </Box>
      <HStack gap={2} alignItems="center">
        <VStack alignItems="flex-end" flexShrink={0}>
          {shouldShowOriginalPrice && (
            <Amount
              amount={variation.original_price}
              isLineThrough
              color={colors.grey500}
            />
          )}
          <Amount
            amount={variation.sale_price}
            color={colors.red600}
            isBold
          />
          {!variation.is_out_of_stock && remainingQuantity <= LOW_STOCK_THRESHOLD && (
            <Text variant="caption" color={colors.green400}>
              剩餘數量：
              {remainingQuantity}
            </Text>
          )}
        </VStack>
        <Box width={88} display="flex" justifyContent="center">
          {variation.is_out_of_stock
            ? (
              <Text variant="caption" color={colors.grey600}>已售完</Text>
            )
            : (
              <NumberInput
                max={variation.stock}
                value={currentQuantity}
                onChange={onQuantityChange}
                xStyles={{ wrapper: styles.inputWrapper, input: styles.input }}
              />
            )}
        </Box>
      </HStack>
    </HStack>
  )
}
